import produce from 'immer';
import { Action, createModel } from '@rematch/core';
import { deprecatedCheckoutAppointment } from '../../api/Checkout/CheckoutAppointment';
import {
  ProAppointment,
  ProAppointmentDetails,
  createProAppointment,
  getProAppointmentDetails,
  updateProAppointment,
  ClientCardOnFile,
  removeSmartPrice,
  PaymentOption,
  BaseTimeBlock,
} from '../../api/Providers/Appointments';
import { cancelAppointmentById } from '../../api/Providers/Appointments/AppointmentsCancellation';
import { markNoShowAppointment } from '../../api/Providers/Appointments/AppointmentsNoShow';
import { deleteAppointment } from '../../api/Providers/Appointments/AppointmentsDelete';
import { undoCheckout } from '../../api/Providers/Appointments/AppointmentsUndoCheckout';
import nonCriticalException from '../../modules/exceptionLogger';
import {
  buildCheckoutDetails,
  canUseCardOnFile,
  CheckoutDetails,
  CheckoutState,
  deletePaymentMethod,
  getPaymentType,
  paymentTypesAreDiscountable,
} from '../../modules/provider/appointmentCheckout';
import {
  changeAppointmentDate,
  formatIntakeFormResponse,
  mergePromotionObjects,
} from '../../modules/provider/Appointments';
import { loadCard } from '../../modules/provider/ClientCard';
import { selectors as userSelectors } from '../CurrentUser.model';
import type { RootModel } from '../models';
import { ICard, InstrumentType } from '../PaymentMethods';
import type { PrivilegedProvider } from '../../api/Providers';
import { getFormattedPromotions } from '../../api/Providers/Promotions';
import { getIntakeFormResponse } from '../../api/Providers/IntakeFormResponse';
import {
  AppointmentTotals,
  AppointmentTotalsParams,
  fetchAppointmentTotals,
} from '../../api/Providers/Appointments/AppointmentTotals';
import { AppointmentProduct } from '../../api/Providers/Products';
import { USE_NEW_CHECKOUT_FEATURE_FLAG } from './constants';
import {
  CheckoutResponse,
  checkoutAppointment,
  isCheckoutPaymentError,
  isCheckoutSuccess,
} from '../../api/Providers/Appointments/Checkout';
import {
  RefundApiResponse,
  getRefunds,
  refundAppointment,
} from '../../api/Appointment/Refund';
import { AppointmentTransactionResponse, fetchAppointmentTransactions } from '../../api/Providers/Appointments/Transactions';

export type ProAppointmentUpdatePayload = Partial<ProAppointment & {
  waitlist_id: ProAppointmentDetails['waitlist_id'];
  smart_price_source: string;
}>;

export type ProviderManageAppointmentState = {
  selectedAppointmentId: number | null;
  selectedAppointmentCancelReason?: string;
  providerId: number | null;
  details: {
    [id: number]: ProAppointmentDetails;
  };
  loading: boolean;

  checkout: CheckoutState;
  totals?: AppointmentTotals;
  refunds?: RefundApiResponse[];
  transactions?: AppointmentTransactionResponse;
};

type AppointmentPayload = {
  appointment: ProAppointmentDetails;
};

type OnLandPayload = {
  appointmentId: number;
};

type OnLoadedPayload = AppointmentPayload & {
  totals?: AppointmentTotals;
};

type OnLoadRefundsPayload = {
  refunds: RefundApiResponse[];
};

type OnLoadTransactionsPayload = {
  transactions: AppointmentTransactionResponse;
};

type AppointmentIdPayload = {
  appointmentId: number;
};

type IdPayload = AppointmentIdPayload & {
  providerId: number;
};

type LoadPayload = IdPayload & {
  tip?: string;
};

type RefundPayload = IdPayload & {
  amount: number;
  reason: string;
  notes?: string;
};

type CancelPayload = IdPayload & {
  cancelAll?: boolean;
};

type NoShowPayload = IdPayload & {
  delete?: boolean;
  charge?: number | string;
};

type UpdatePayload = {
  appointment: ProAppointmentUpdatePayload;
  providerId: number;
};

type CheckoutPayload = {
  signature?: string;
};

export type UpdateCheckoutPayload = {
  tip?: string;
  chargeTipLater?: boolean;
  costOverride?: string;
  productCostOverride?: string;
  selectedPaymentType?: PaymentOption;
  products?: AppointmentProduct[];
  services?: BaseTimeBlock[];
};

type OnCheckoutUpdatedPayload = {
  tip?: number;
  chargeTipLater?: boolean;
  costOverride?: number;
  productCostOverride?: number;
  selectedPaymentType?: PaymentOption;
  products?: AppointmentProduct[];
  services?: BaseTimeBlock[];
  totals: AppointmentTotals;
};

type UpdatePaymentTypePayload = {
  paymentType: PaymentOption;
  card: ICard | null;
};

type OnPaymentTypeUpdatedPayload = UpdatePaymentTypePayload & {
  totals: AppointmentTotals;
};

type CreateCheckoutPayload = UpdatePaymentTypePayload & {
  discountAmount: number;
  discountCode?: string;
  tip: string;
  cardOnFile?: ClientCardOnFile | null;
  totals: AppointmentTotals;
};

type UpdateTipPayload = {
  tip: string | number;
  chargeTipLater?: boolean;
};

type UpdateCostPayload = {
  cost: string;
  productCost: string;
  overrideCost?: boolean;
};

type OnCostUpdatedPayload = UpdateCostPayload & {
  totals: AppointmentTotals;
};

type OnTipUpdatedPayload = UpdateTipPayload & {
  totals: AppointmentTotals;
};

export type Discount = {
  discountCode?: string | null;
  discountAmount: number;
};

function getInitialState(): ProviderManageAppointmentState {
  return {
    providerId: null,
    selectedAppointmentId: null,
    selectedAppointmentCancelReason: undefined,
    loading: false,
    details: {},
    checkout: {
      discountAmount: 0,
      tip: '0',
      chargeTipLater: false,
      paymentType: null,
      card: undefined,
      cardOnFile: undefined,
    },
    refunds: undefined,
    transactions: undefined,
  };
}

function getDiscountFromCheckout({
  paymentType,
  discountAmount,
  discountCode,
}: CheckoutState): Discount {
  if (!paymentTypesAreDiscountable(paymentType)) {
    return {
      discountAmount: 0,
      discountCode: null,
    };
  }
  return {
    discountAmount,
    discountCode,
  };
}

interface AppointmentLandResult {
  appointment: ProAppointmentDetails;
  provider: PrivilegedProvider;
}

type CreateCheckoutResult = Action<UpdatePaymentTypePayload & {
  discountAmount: number;
  discountCode?: string;
  tip: string;
  cardOnFile?: ClientCardOnFile;
}, void>;

/**
 *  providerManageAppointment model
 *  exposes a model that allows a provider to manage an appointment -
 *  used in providerDetails, providerAppointmentEdit, providerAppointmentCreate and
 *  the providerCalendar.
 * */
export const providerManageAppointment = createModel<RootModel>()({
  name: 'providerManageAppointment',

  state: getInitialState() as ProviderManageAppointmentState,

  reducers: {
    'user/logout': () => getInitialState(),

    onLoad(
      state: ProviderManageAppointmentState,
      payload: Partial<IdPayload>,
    ): ProviderManageAppointmentState {
      return {
        ...state,
        ...payload,
        loading: true,
      };
    },

    onLoaded: produce<ProviderManageAppointmentState, [OnLoadedPayload]>((
      state: ProviderManageAppointmentState,
      { appointment, totals }: OnLoadedPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      state.selectedAppointmentId = appointment.id;

      if (totals) {
        state.totals = totals;
      }

      state.loading = false;
      return state;
    }),

    onLoadRefunds: produce<ProviderManageAppointmentState, [OnLoadRefundsPayload]>((
      state: ProviderManageAppointmentState,
      { refunds }: OnLoadRefundsPayload,
    ): ProviderManageAppointmentState => {
      state.refunds = refunds;
      return state;
    }),

    onLoadTransactions: produce<ProviderManageAppointmentState, [OnLoadTransactionsPayload]>((
      state: ProviderManageAppointmentState,
      { transactions }: OnLoadTransactionsPayload,
    ): ProviderManageAppointmentState => {
      state.transactions = transactions;
      return state;
    }),

    onLand: (
      state: ProviderManageAppointmentState,
      { appointmentId }: OnLandPayload,
    ): ProviderManageAppointmentState => ({
      ...getInitialState(),
      providerId: state.providerId,
      selectedAppointmentId: appointmentId,
    }),

    onLanded: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      state.selectedAppointmentId = appointment.id;
      state.loading = false;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onCreated: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onUpdated: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onRescheduled: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onCancellation: produce<ProviderManageAppointmentState, [AppointmentIdPayload]>((
      state: ProviderManageAppointmentState,
      { appointmentId }: AppointmentIdPayload,
    ) => {
      delete state.details[appointmentId];
      return state;
    }),

    onCancelReasonSubmitted: produce<ProviderManageAppointmentState, [string]>((
      state: ProviderManageAppointmentState,
      selected_reason: string,
    ) => {
      state.selectedAppointmentCancelReason = selected_reason;
      return state;
    }),

    onCheckoutCreated: produce<ProviderManageAppointmentState, [CreateCheckoutPayload]>((
      state: ProviderManageAppointmentState,
      {
        discountAmount,
        tip,
        discountCode,
        paymentType,
        card,
        cardOnFile,
        totals,
      }: CreateCheckoutPayload,
    ) => {
      state.checkout.discountAmount = discountAmount;
      state.checkout.discountCode = discountCode;
      state.checkout.tip = tip;
      state.checkout.paymentType = paymentType;
      state.checkout.card = card;
      state.checkout.cardOnFile = cardOnFile;
      state.totals = totals;
      return state;
    }),

    onCheckoutUpdated: produce<ProviderManageAppointmentState, [OnCheckoutUpdatedPayload]>((
      state: ProviderManageAppointmentState,
      {
        tip,
        chargeTipLater,
        costOverride,
        productCostOverride,
        selectedPaymentType,
        services,
        products,
        totals,
      }: OnCheckoutUpdatedPayload,
    ) => {
      state.checkout.tip = typeof tip !== 'undefined' ? String(tip) : state.checkout.tip;
      state.checkout.chargeTipLater = (
        typeof chargeTipLater !== 'undefined'
          ? chargeTipLater
          : state.checkout.chargeTipLater
      );
      state.checkout.costOverride = (
        typeof costOverride !== 'undefined'
          ? costOverride
          : state.checkout.costOverride
      );
      state.checkout.productCostOverride = (
        typeof productCostOverride !== 'undefined'
          ? productCostOverride
          : state.checkout.productCostOverride
      );
      state.checkout.paymentType = selectedPaymentType || state.checkout.paymentType;
      state.checkout.selectedProducts = products || state.checkout.selectedProducts;
      state.checkout.selectedServices = services || state.checkout.selectedServices;
      state.totals = totals;
      state.loading = false;
    }),

    onTipUpdated: produce<ProviderManageAppointmentState, [OnTipUpdatedPayload]>((
      state: ProviderManageAppointmentState,
      {
        tip,
        totals,
        chargeTipLater = false,
      }: OnTipUpdatedPayload,
    ) => {
      const parsedTip = Number(tip);
      const correctedTip = Number.isNaN(parsedTip) ? 0 : parsedTip;
      const roundedTip = correctedTip.toFixed(2);
      state.checkout.tip = roundedTip;
      state.checkout.chargeTipLater = chargeTipLater;
      state.totals = totals;
    }),

    onCostUpdated: produce<ProviderManageAppointmentState, [OnCostUpdatedPayload]>((
      state: ProviderManageAppointmentState,
      {
        cost,
        productCost,
        overrideCost = false,
        totals,
      }: OnCostUpdatedPayload,
    ) => {
      const parsedCost = Number(cost);
      const parsedProductCost = Number(productCost);
      if (overrideCost && !Number.isNaN(parsedCost)) {
        state.checkout.costOverride = parsedCost;
      } else {
        delete state.checkout.costOverride;
      }
      state.checkout.productCostOverride = parsedProductCost;
      state.totals = totals;
    }),

    onPaymentTypeUpdated: produce<ProviderManageAppointmentState, [OnPaymentTypeUpdatedPayload]>((
      state: ProviderManageAppointmentState,
      {
        paymentType, card, totals,
      }: OnPaymentTypeUpdatedPayload,
    ) => {
      state.checkout.paymentType = paymentType;
      state.checkout.card = card;
      state.totals = totals;
    }),
  },

  effects: dispatch => ({
    async land(
      { appointmentId }: AppointmentIdPayload,
      rootState,
    ): Promise<AppointmentLandResult> {
      dispatch.providerManageAppointment.onLand({ appointmentId });
      const providerId = userSelectors.getSuperAwareProviderId(rootState);
      let appointment = await getProAppointmentDetails(providerId, appointmentId);

      // if there are promotions, map the IDs to objects
      if (appointment.pro_promotion_discounts.length) {
        appointment = mergePromotionObjects(
          appointment,
          await getFormattedPromotions(providerId, {}),
        );
      }

      if (appointment.intake_form_id) {
        const intakeFormResponse = await getIntakeFormResponse(appointment.intake_form_id);
        if (intakeFormResponse) {
          appointment.intake_form_response = formatIntakeFormResponse(
            appointment,
            intakeFormResponse,
          );
        }
      }

      const refunds = await getRefunds(appointment.id);
      await dispatch.providerManageAppointment.onLoadRefunds({ refunds });

      const provider = await dispatch.providers.loadProvider({
        providerId,
        setActive: true,
        noLoading: true,
      }) as PrivilegedProvider;
      await dispatch.providerManageAppointment.createCheckout({ appointment, provider });
      dispatch.providerManageAppointment.onLanded({
        appointment,
      });
      return {
        appointment,
        provider,
      };
    },

    async load({
      appointmentId,
      providerId,
      tip,
    }: LoadPayload): Promise<ProAppointment> {
      dispatch.providerManageAppointment.onLoad({ appointmentId, providerId });
      await dispatch.proCheckoutSettings.load({
        providerId,
      });
      let appointment = await getProAppointmentDetails(providerId, appointmentId);

      // if there are promotions, map the IDs to objects
      if (appointment.pro_promotion_discounts.length) {
        appointment = mergePromotionObjects(
          appointment,
          await getFormattedPromotions(providerId, {}),
        );
      }

      if (appointment.intake_form_id) {
        const intakeFormResponse = await getIntakeFormResponse(appointment.intake_form_id);
        if (intakeFormResponse) {
          appointment.intake_form_response = formatIntakeFormResponse(
            appointment,
            intakeFormResponse,
          );
        }
      }

      const refunds = await getRefunds(appointment.id);
      await dispatch.providerManageAppointment.onLoadRefunds({ refunds });

      const appointmentTotals = await fetchAppointmentTotals(
        appointment.provider,
        appointment.id,
        {
          tip: Number(
            tip
            || appointment.checkout?.tip
            || 0,
          ),
        },
      );

      dispatch.providerManageAppointment.onLoaded({
        appointment,
        totals: appointmentTotals,
      });
      return appointment;
    },

    async create(
      { appointment, providerId }: UpdatePayload,
      rootState,
    ): Promise<ProAppointment> {
      const response = await createProAppointment(providerId, {
        ...appointment,
        booked_by: rootState?.user?.user_id ?? rootState?.user?.userId,
      });

      dispatch.providerManageAppointment.onCreated({ appointment: response });
      return response;
    },

    async update(
      { appointment, providerId }: UpdatePayload,
      rootState,
    ): Promise<void> {
      const response = await updateProAppointment(providerId, appointment);
      dispatch.providerManageAppointment.onUpdated({ appointment: response });
      await dispatch.providerManageAppointment.load({
        providerId,
        appointmentId: appointment.id,
        tip: rootState.providerManageAppointment.checkout?.tip,
      });
    },

    async cancel({
      appointmentId, providerId, cancelAll,
    }: CancelPayload, rootState) {
      const {
        selectedAppointmentCancelReason,
      } = rootState.providerManageAppointment as ProviderManageAppointmentState;

      const response = await cancelAppointmentById(
        providerId,
        appointmentId,
        cancelAll,
        selectedAppointmentCancelReason,
      );
      dispatch.providerManageAppointment.onCancellation({ appointmentId });
      return response;
    },

    async checkout({ signature }: CheckoutPayload = {}, rootState) {
      // Rebuild all checkoutDetails from store
      const {
        checkout: checkoutState,
        totals: checkoutTotals,
        details,
        selectedAppointmentId,
      } = rootState.providerManageAppointment as ProviderManageAppointmentState;
      const appointment = details[selectedAppointmentId];
      const checkoutDetails = buildCheckoutDetails(
        appointment,
        checkoutState,
        checkoutTotals,
      );

      if (!checkoutDetails) {
        throw new Error('Not enough information to generate checkout');
      }

      let response: CheckoutResponse;
      // Actually check out the appointment
      if (
        Number(checkoutTotals.deposit_amount) > 0
        || rootState.abTest.assignments[USE_NEW_CHECKOUT_FEATURE_FLAG]?.isEnabled
      ) {
        // if there's a deposit, or the flag is on, use the new checkout endpoint
        response = await checkoutAppointment(appointment.provider, appointment.id, {
          ...checkoutDetails,
          signature,
        });
      } else {
        // if no deposit or flag is off, use the old checkout endpoint
        response = await deprecatedCheckoutAppointment(appointment.id, {
          ...checkoutDetails,
          signature,
        });
      }

      if (!isCheckoutSuccess(response)) {
        if (isCheckoutPaymentError(response)) {
          throw new Error(response.error_message);
        }

        // eslint-disable-next-line no-underscore-dangle
        throw new Error(response?.__all__?.[0] || 'Error occurred checking out this appointment');
      }

      const updatedAppt = await getProAppointmentDetails(appointment.provider, appointment.id);
      dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });

      // localstorage cleanup
      deletePaymentMethod(appointment.id);
      return appointment;
    },

    async undoCheckout({ appointmentId, providerId }: IdPayload) {
      await undoCheckout(providerId, appointmentId);
      const updatedAppt = await getProAppointmentDetails(providerId, appointmentId);
      dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });
      return updatedAppt;
    },

    async markNoShow({
      appointmentId,
      providerId,
      delete: markDeleted,
      charge,
    }: NoShowPayload, rootState) {
      const opts: { charge?: string | number; ncd_cancellation_survey_reason?: string } = {
        charge,
      };
      if (charge) {
        opts.charge = Number(charge).toFixed(2);
      }

      const {
        selectedAppointmentCancelReason,
      } = rootState.providerManageAppointment as ProviderManageAppointmentState;

      if (selectedAppointmentCancelReason) {
        opts.ncd_cancellation_survey_reason = selectedAppointmentCancelReason;
      }

      if (markDeleted) {
        await deleteAppointment(appointmentId, opts);
      } else {
        await markNoShowAppointment(appointmentId, opts);
      }
      const updatedAppt = await getProAppointmentDetails(providerId, appointmentId);
      await dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });
      dispatch.providerClients.loadClient({
        providerId,
        clientId: updatedAppt.client?.id,
      });
      return updatedAppt;
    },

    async removeSmartPricing({
      appointmentId,
      providerId,
    }: IdPayload, rootState) {
      await removeSmartPrice(providerId, appointmentId);
      await dispatch.providerManageAppointment.load({
        appointmentId,
        providerId,
        tip: rootState.providerManageAppointment.checkout?.tip,
      });
    },

    async loadTransactions({ appointmentId, providerId }: IdPayload) {
      const transactions = await fetchAppointmentTransactions(providerId, appointmentId);
      await dispatch.providerManageAppointment.onLoadTransactions({ transactions });
    },

    async reschedule({
      appointmentId, providerId, newDate,
    }: IdPayload & { newDate: string }): Promise<any> {
      const apptPayload = changeAppointmentDate(
        await getProAppointmentDetails(providerId, appointmentId),
        newDate,
      );
      const response = await updateProAppointment(providerId, apptPayload);
      dispatch.providerManageAppointment.onRescheduled({ appointment: response });
      return response;
    },

    async createCheckout({
      appointment,
      provider,
    }: {
      appointment: ProAppointmentDetails;
      provider: PrivilegedProvider;
    }, rootState): Promise<CreateCheckoutResult> {
      const proCheckoutSettingsPromise = dispatch.proCheckoutSettings.load({
        providerId: appointment.provider,
      });

      const appointmentTotals = await fetchAppointmentTotals(
        appointment.provider,
        appointment.id,
        {
          tip: Number(
            rootState.providerManageAppointment.checkout?.tip
            || appointment.checkout?.tip
            || 0,
          ),
        },
      );

      let card: ClientCardOnFile | null = null;
      if (appointment.client?.id) {
        try {
          [card] = await loadCard(appointment.client?.id, appointment.id);
        } catch (e) {
          nonCriticalException(e);
          console.error('Could not load the client card on file', e);
        }
      }
      const paymentType = getPaymentType(appointment, provider, card);
      const cardForCheckout: ICard | null = card ? {
        ...card,
        is_default: false,
        instrument_type: InstrumentType.Card,
      } : null;

      await proCheckoutSettingsPromise;

      return dispatch.providerManageAppointment.onCheckoutCreated({
        discountAmount: Number(appointmentTotals.discount_amount || 0),
        discountCode: appointmentTotals.discount_code,
        tip: appointment.checkout?.tip || '0',
        paymentType,
        card: canUseCardOnFile(appointment, provider) ? cardForCheckout : null,
        cardOnFile: card,
        totals: appointmentTotals,
      });
    },

    async updateCheckout({
      tip,
      chargeTipLater,
      costOverride,
      productCostOverride,
      selectedPaymentType,
      products,
      services,
    }: UpdateCheckoutPayload, rootState) {
      dispatch.providerManageAppointment.onLoad({});
      const {
        checkout,
        details,
        selectedAppointmentId,
      } = rootState.providerManageAppointment;
      const appointment = details[selectedAppointmentId];

      const fetchParams: AppointmentTotalsParams = {
        tip: checkout.tip ? Number(checkout.tip) : undefined,
        cost_override: checkout.costOverride ? Number(checkout.costOverride) : undefined,
        product_cost_override: (
          checkout.productCostOverride ? Number(checkout.productCostOverride) : undefined
        ),
        selected_payment_type: checkout.paymentType,
        service_ids: checkout.selectedServices?.map(service => String(service.service)),
        product_prices: checkout.selectedProducts?.map(product => String(product.price)),
      };

      // make sure we can set tip to 0
      if (tip || typeof tip === 'number' || typeof tip === 'string') {
        fetchParams.tip = Number(Number(tip).toFixed(2));
      }

      if (costOverride || typeof costOverride === 'number') {
        fetchParams.cost_override = Number(costOverride);
      }

      if (productCostOverride || typeof productCostOverride === 'number') {
        fetchParams.product_cost_override = Number(productCostOverride);
      }

      if (selectedPaymentType) {
        fetchParams.selected_payment_type = selectedPaymentType;
      }

      if (products) {
        fetchParams.product_prices = products.map(product => String(product.price));
      }

      if (services) {
        fetchParams.service_ids = services.map(service => String(service.service));
      }

      const appointmentTotals = await fetchAppointmentTotals(
        appointment.provider,
        appointment.id,
        fetchParams,
      );

      dispatch.providerManageAppointment.onCheckoutUpdated({
        tip: fetchParams.tip,
        chargeTipLater,
        costOverride: fetchParams.cost_override,
        productCostOverride: fetchParams.product_cost_override,
        selectedPaymentType,
        products,
        services,
        totals: appointmentTotals,
      });
    },

    async updatePaymentType({
      paymentType,
      card,
    }: UpdatePaymentTypePayload, rootState) {
      const {
        checkout,
        details,
        selectedAppointmentId,
      } = rootState.providerManageAppointment;
      const appointment = details[selectedAppointmentId];

      const fetchParams: AppointmentTotalsParams = {
        tip: checkout.tip ? Number(checkout.tip) : undefined,
        cost_override: checkout.costOverride ? Number(checkout.costOverride) : undefined,
        product_cost_override: (
          checkout.productCostOverride ? Number(checkout.productCostOverride) : undefined
        ),
        selected_payment_type: paymentType,
        service_ids: checkout.selectedServices?.map(service => String(service.service)),
        product_prices: checkout.selectedProducts?.map(product => String(product.price)),
      };

      const appointmentTotals = await fetchAppointmentTotals(
        appointment.provider,
        appointment.id,
        fetchParams,
      );

      dispatch.providerManageAppointment.onPaymentTypeUpdated({
        paymentType,
        card,
        totals: appointmentTotals,
      });
    },

    async refund({
      appointmentId,
      providerId,
      amount,
      reason,
      notes,
    }: RefundPayload, rootState) {
      // remember if we had loaded transactions, since they get dropped on load
      const hadTransactions = !!rootState.providerManageAppointment.transactions;
      await refundAppointment(appointmentId, {
        amount, reason, notes,
      });

      await dispatch.providerManageAppointment.load({ appointmentId, providerId });

      if (hadTransactions) {
        await dispatch.providerManageAppointment.loadTransactions({ appointmentId, providerId });
      }
    },
  }),

  selectors: (slice, createSelector, hasProps) => ({
    // returns the selected appointment
    appointment: models => createSelector(
      slice(state => state.details),
      models.providerCalendar.appointmentsById,
      slice(state => state.selectedAppointmentId),
      (appointments, calendarAppts, apptId): ProAppointmentDetails => (
        appointments[apptId] ?? calendarAppts[apptId] as ProAppointmentDetails
      ),
    ),

    forId: hasProps((models: any, id: number) => (
      createSelector(
        slice(state => state.details),
        details => details[id],
      )
    )),

    applicableDiscount() {
      return createSelector(
        slice(state => state.checkout),
        getDiscountFromCheckout,
      );
    },

    checkoutDetails(models) {
      return createSelector(
        slice,
        models.providerCalendar.appointmentsById,
        (
          manageAppointmentState,
          calendarAppts,
        ): CheckoutDetails | undefined => {
          const apptId = manageAppointmentState.selectedAppointmentId;
          const appt = (
            manageAppointmentState.details[apptId] ?? calendarAppts[apptId]
          ) as ProAppointmentDetails;
          const {
            checkout: checkoutState,
            totals: checkoutTotals,
          } = manageAppointmentState;

          return buildCheckoutDetails(
            appt,
            checkoutState,
            checkoutTotals,
          );
        },
      );
    },

    isUsingCardOnFile() {
      return createSelector(
        slice(state => state.checkout.card),
        slice(state => state.checkout.cardOnFile),
        (checkoutCard, cardOnFile): boolean => (
          !!checkoutCard
          && checkoutCard.id === cardOnFile?.id
        ),
      );
    },
  }),
});
